import "./styles.css";
import { loadStoreCatalog } from "./api/catalog";
import { t, type MessageKey } from "./i18n";
import { ProductCaseViewer } from "./scene/ProductCaseViewer";
import { millisecondsUntilNextCatalogRefresh } from "./lib/catalogSchedule";
import { StoreScene } from "./scene/StoreScene";
import type { CatalogItem, CatalogSection, Language } from "./types";

type Quality = "low" | "medium" | "high";

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) throw new Error("App root is missing");

app.innerHTML = `
  <main class="app-shell">
    <section class="store-screen" aria-label="QUIYA 3D store">
      <canvas id="store-canvas" aria-label="3D video store"></canvas>
      <div class="scene-grade" aria-hidden="true"></div>
      <div class="quiya-cursor" id="quiya-cursor" aria-hidden="true"><span></span></div>

      <header class="store-header">
        <div class="store-brand" aria-label="QUIYA">
          <img class="brand-mark" src="/quiya-logo-mark.svg" alt="" width="44" height="44" />
          <span class="brand-copy"><span class="brand-word">QUIYA</span></span>
        </div>
        <div class="header-actions">
          <button class="language-toggle" type="button" data-language-toggle>EN</button>
          <button class="menu-button" id="menu-button" type="button" data-i18n-aria="menu" aria-expanded="false"><span></span><span></span></button>
        </div>
      </header>

      <aside class="quick-menu" id="quick-menu" hidden>
        <p>QUIYA / STORE MENU</p>
        <button type="button" id="quality-button"><span><b data-i18n="quality"></b><small data-i18n="qualityNote"></small></span><strong id="quality-value">MEDIUM</strong></button>
        <button type="button" id="floor-guides-button" role="switch" aria-checked="false"><span><b data-i18n="hideFloorGuides"></b><small data-i18n="hideFloorGuidesNote"></small></span><strong id="floor-guides-value">OFF</strong></button>
        <button type="button" id="reset-button" data-i18n="resetPosition"></button>
        <button type="button" id="help-button" data-i18n="help"></button>
        <button type="button" id="about-button" data-i18n="about"></button>
      </aside>

      <div class="mobile-joystick" id="mobile-joystick" aria-hidden="true">
        <div class="joystick-base"><button class="joystick-thumb" type="button" tabindex="-1" data-i18n-aria="mobileMove"></button></div>
      </div>

      <div class="scene-loader" id="scene-loader" role="status">
        <span class="loader-line"></span>
        <strong>QUIYA</strong>
        <small data-i18n="opening"></small>
      </div>

      <div class="fatal-message" id="fatal-message" hidden>
        <strong>WEBGL ERROR</strong>
        <p data-i18n="webglUnavailable"></p>
      </div>
    </section>

    <dialog class="product-dialog" id="product-dialog">
      <div class="dialog-handle" aria-hidden="true"></div>
      <div class="product-layout">
        <div class="product-case-column">
          <div class="product-case-stage">
            <canvas id="product-case-canvas" data-i18n-aria="caseViewer"></canvas>
          </div>
          <div class="case-drag-hint"><span aria-hidden="true">↔</span><span data-i18n="rotateCase"></span></div>
        </div>
        <div class="product-copy">
          <h2 id="product-title"></h2>
          <a class="fanza-button" id="fanza-link" target="_blank" rel="sponsored noopener noreferrer" data-i18n="openFanza"></a>
          <dl class="product-facts" id="product-facts"></dl>
          <div class="price-notice-row">
            <p class="price-notice" data-i18n="priceNotice"></p>
            <small class="ad-label"><span class="info-mark" aria-hidden="true">i</span><span data-i18n="adLabel"></span></small>
          </div>
        </div>
      </div>
    </dialog>

    <dialog class="info-dialog age-dialog" id="age-dialog">
      <p class="eyebrow" data-i18n="ageEyebrow"></p>
      <h2 data-i18n="ageTitle"></h2>
      <p class="age-description" data-i18n="ageDescription"></p>
      <div class="age-actions">
        <button class="primary-button" id="age-accept" type="button" data-i18n="enter"></button>
        <button class="secondary-button" id="age-decline" type="button" data-i18n="leave"></button>
      </div>
    </dialog>

    <dialog class="info-dialog help-dialog" id="help-dialog">
      <p class="eyebrow">QUIYA GUIDE</p>
      <h2 data-i18n="helpTitle"></h2>
      <div class="help-body" id="help-body"></div>
      <div class="dialog-actions">
        <button class="primary-button" type="button" data-close-help data-i18n="understood"></button>
      </div>
    </dialog>
  </main>
`;

let language: Language = localStorage.getItem("quiya-language") === "en" ? "en" : "ja";
const savedQuality = localStorage.getItem("quiya-3d-quality-v1");
let quality: Quality = savedQuality === "low" || savedQuality === "high" ? savedQuality : "medium";
const ageStorageKey = "quiya-age-verified-v1";
const ageWasConfirmed = localStorage.getItem(ageStorageKey) === "true";
const floorGuidesStorageKey = "quiya-hide-floor-guides-v1";
let floorGuidesHidden = localStorage.getItem(floorGuidesStorageKey) === "true";
let store: StoreScene | null = null;
let loadingCatalog = false;
let selectedItem: CatalogItem | null = null;
// ダイアログを開くタップのクリックが、直後に描画されたダイアログ内の
// アフィリエイトリンクへ抜ける（iOSのモーダル通過バグ）のを防ぐため、
// ダイアログを開いた直後のクリックを一時的に抑制する。
let suppressDialogClick = false;
let dailyRefreshTimer: number | null = null;
let catalogWarmupPromise: Promise<CatalogSection[]> | null = null;

const canvas = requireElement<HTMLCanvasElement>("#store-canvas");
const sceneLoader = requireElement<HTMLElement>("#scene-loader");
const quickMenu = requireElement<HTMLElement>("#quick-menu");
const menuButton = requireElement<HTMLButtonElement>("#menu-button");
const productDialog = requireElement<HTMLDialogElement>("#product-dialog");
const helpDialog = requireElement<HTMLDialogElement>("#help-dialog");
const ageDialog = requireElement<HTMLDialogElement>("#age-dialog");
const caseViewer = new ProductCaseViewer(requireElement<HTMLCanvasElement>("#product-case-canvas"));

function requireElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing element: ${selector}`);
  return element;
}

function renderHelpBody(): void {
  const body = document.getElementById("help-body");
  if (!body) return;
  body.replaceChildren();
  for (const line of t(language, "helpBody").split("\n")) {
    if (line.trim() === "") {
      const gap = document.createElement("div");
      gap.className = "help-gap";
      body.append(gap);
      continue;
    }
    const row = document.createElement("p");
    row.className = "help-row";
    row.textContent = line;
    body.append(row);
  }
}

function applyLanguage(): void {
  document.documentElement.lang = language;
  document.querySelectorAll<HTMLElement>("[data-i18n]").forEach((element) => {
    element.textContent = t(language, element.dataset.i18n as MessageKey);
  });
  document.querySelectorAll<HTMLElement>("[data-i18n-aria]").forEach((element) => {
    element.setAttribute("aria-label", t(language, element.dataset.i18nAria as MessageKey));
  });
  document.querySelectorAll<HTMLButtonElement>("[data-language-toggle]").forEach((button) => {
    button.textContent = language === "ja" ? "EN" : "日本語";
  });
  store?.setLanguage(language);
  if (selectedItem && productDialog.open) caseViewer.show(selectedItem, language);
  renderHelpBody();
}

function toggleLanguage(): void {
  language = language === "ja" ? "en" : "ja";
  localStorage.setItem("quiya-language", language);
  applyLanguage();
}

async function loadCatalogAfterAgeConfirmation(): Promise<void> {
  if (loadingCatalog) return;
  loadingCatalog = true;
  try {
    await beginCatalogWarmup();
  } catch {
    // The store keeps its neutral placeholder cases when the catalog is down.
  }
  if (dailyRefreshTimer === null) {
    scheduleDailyCatalogRefresh();
  }
}

function scheduleDailyCatalogRefresh(): void {
  if (dailyRefreshTimer !== null) window.clearTimeout(dailyRefreshTimer);
  // Start just after the 04:00 JST boundary so every request uses the new
  // catalog date even if device and edge clocks differ by a few milliseconds.
  const delay = millisecondsUntilNextCatalogRefresh() + 1_000;
  dailyRefreshTimer = window.setTimeout(async () => {
    await refreshDailyCatalog();
    scheduleDailyCatalogRefresh();
  }, delay);
}

function beginCatalogWarmup(): Promise<CatalogSection[]> {
  if (!catalogWarmupPromise) {
    catalogWarmupPromise = loadStoreCatalog().then((sections) => {
      store?.setSections(sections);
      return sections;
    });
  }
  return catalogWarmupPromise;
}

async function refreshDailyCatalog(): Promise<void> {
  try {
    catalogWarmupPromise = loadStoreCatalog();
    const sections = await catalogWarmupPromise;
    store?.setSections(sections);
  } catch {
    // Keep the current shelf when a scheduled refresh cannot reach the API.
  }
}

function openProduct(item: CatalogItem): void {
  selectedItem = item;
  requireElement<HTMLElement>("#product-title").textContent = item.title;

  const facts = requireElement<HTMLElement>("#product-facts");
  facts.replaceChildren();
  addFact(facts, t(language, "price"), item.price.display ?? "—", "price");
  addFact(facts, t(language, "releaseDate"), formatDate(item.releaseDate));
  addFact(facts, t(language, "performer"), formatPerformers(item.performers));
  addFact(facts, t(language, "maker"), item.maker ?? "—");
  addFact(facts, t(language, "genres"), item.genres.map((genre) => genre.name).join(" / ") || "—");
  addFact(facts, t(language, "productCode"), item.id.toUpperCase());

  const link = requireElement<HTMLAnchorElement>("#fanza-link");
  link.href = item.affiliateUrl;
  if (!productDialog.open) productDialog.showModal();
  // 開いた直後のタップのクリックがリンクへ抜けるのを防ぐ（iOSモーダル通過バグ）。
  suppressDialogClick = true;
  window.setTimeout(() => { suppressDialogClick = false; }, 600);
  window.requestAnimationFrame(() => caseViewer.show(item, language));
}

function closeProduct(): void {
  caseViewer.hide();
  selectedItem = null;
  productDialog.close();
}

function addFact(root: HTMLElement, label: string, value: string, className = ""): void {
  const wrapper = document.createElement("div");
  if (className) wrapper.className = className;
  const term = document.createElement("dt");
  term.textContent = label;
  const description = document.createElement("dd");
  description.textContent = value;
  wrapper.append(term, description);
  root.append(wrapper);
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  const parts = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const date = parts ? new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3])) : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(language === "ja" ? "ja-JP" : "en-US", { year: "numeric", month: "short", day: "numeric" }).format(date);
}

function formatPerformers(performers: string[]): string {
  if (performers.length === 0) return "—";
  const visible = performers.slice(0, 8).join(language === "ja" ? "、" : ", ");
  const remaining = performers.length - 8;
  if (remaining <= 0) return visible;
  return language === "ja" ? `${visible}、ほか${remaining}名` : `${visible} +${remaining} more`;
}

function requestAgeConfirmation(): void {
  if (!ageDialog.open) ageDialog.showModal();
}

function setupJoystick(): void {
  const joystick = requireElement<HTMLElement>("#mobile-joystick");
  const base = requireElement<HTMLElement>(".joystick-base");
  const thumb = requireElement<HTMLButtonElement>(".joystick-thumb");
  let pointerId: number | null = null;

  const update = (event: PointerEvent): void => {
    const rect = base.getBoundingClientRect();
    const maxDistance = Math.max(1, (rect.width - thumb.offsetWidth) / 2);
    const rawX = event.clientX - (rect.left + rect.width / 2);
    const rawY = event.clientY - (rect.top + rect.height / 2);
    const distance = Math.hypot(rawX, rawY);
    const scale = distance > maxDistance ? maxDistance / distance : 1;
    const x = rawX * scale;
    const y = rawY * scale;
    thumb.style.transform = `translate(${x}px, ${y}px)`;
    store?.setMoveInput(x / maxDistance, -y / maxDistance);
  };

  const release = (event: PointerEvent): void => {
    if (pointerId !== event.pointerId) return;
    pointerId = null;
    thumb.style.transform = "translate(0, 0)";
    joystick.classList.remove("is-active");
    store?.setMoveInput(0, 0);
  };

  base.addEventListener("pointerdown", (event) => {
    pointerId = event.pointerId;
    base.setPointerCapture(event.pointerId);
    joystick.classList.add("is-active");
    update(event);
    event.preventDefault();
    event.stopPropagation();
  });
  base.addEventListener("pointermove", (event) => {
    if (pointerId !== event.pointerId) return;
    update(event);
    event.preventDefault();
    event.stopPropagation();
  });
  base.addEventListener("pointerup", release);
  base.addEventListener("pointercancel", release);
}

function setupCustomCursor(): void {
  const cursor = requireElement<HTMLElement>("#quiya-cursor");
  window.addEventListener("pointermove", (event) => {
    if (event.pointerType === "touch") return;
    cursor.style.transform = `translate3d(${event.clientX}px, ${event.clientY}px, 0)`;
    cursor.classList.add("is-visible");
  });
  window.addEventListener("pointerdown", (event) => {
    if (event.pointerType !== "touch") cursor.classList.add("is-pressed");
  });
  window.addEventListener("pointerup", () => cursor.classList.remove("is-pressed"));
  document.documentElement.addEventListener("mouseleave", () => cursor.classList.remove("is-visible"));
  document.addEventListener("pointerover", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    cursor.classList.toggle("is-target", Boolean(target?.closest("button, a, canvas")));
  });
}

document.querySelectorAll<HTMLButtonElement>("[data-language-toggle]").forEach((button) => button.addEventListener("click", toggleLanguage));
  menuButton.addEventListener("click", () => {
  quickMenu.hidden = !quickMenu.hidden;
  menuButton.setAttribute("aria-expanded", String(!quickMenu.hidden));
});
requireElement<HTMLButtonElement>("#quality-button").addEventListener("click", () => {
  quality = quality === "low" ? "medium" : quality === "medium" ? "high" : "low";
  localStorage.setItem("quiya-3d-quality-v1", quality);
  requireElement<HTMLElement>("#quality-value").textContent = quality.toUpperCase();
  store?.setQuality(quality);
});
requireElement<HTMLElement>("#quality-value").textContent = quality.toUpperCase();
const floorGuidesButton = requireElement<HTMLButtonElement>("#floor-guides-button");
const updateFloorGuidesSetting = (): void => {
  floorGuidesButton.setAttribute("aria-checked", String(floorGuidesHidden));
  requireElement<HTMLElement>("#floor-guides-value").textContent = floorGuidesHidden ? "ON" : "OFF";
  store?.setFloorGuidesHidden(floorGuidesHidden);
};
floorGuidesButton.addEventListener("click", () => {
  floorGuidesHidden = !floorGuidesHidden;
  localStorage.setItem(floorGuidesStorageKey, String(floorGuidesHidden));
  updateFloorGuidesSetting();
});
updateFloorGuidesSetting();
requireElement<HTMLButtonElement>("#reset-button").addEventListener("click", () => {
  store?.resetPosition(); quickMenu.hidden = true; menuButton.setAttribute("aria-expanded", "false");
});
requireElement<HTMLButtonElement>("#help-button").addEventListener("click", () => {
  quickMenu.hidden = true; menuButton.setAttribute("aria-expanded", "false"); helpDialog.showModal();
});
requireElement<HTMLButtonElement>("#about-button").addEventListener("click", () => {
  quickMenu.hidden = true; menuButton.setAttribute("aria-expanded", "false");
  window.location.href = "/about/";
});
document.querySelectorAll<HTMLButtonElement>("[data-close-help]").forEach((button) => button.addEventListener("click", () => helpDialog.close()));
productDialog.addEventListener("click", (event) => {
  if (event.target === productDialog) {
    closeProduct();
    return;
  }
  // ダイアログを開いた直後のうっかりクリック（リンクへの通過）を抑制。
  if (suppressDialogClick) {
    event.preventDefault();
    event.stopPropagation();
    suppressDialogClick = false;
  }
});
productDialog.addEventListener("close", () => {
  caseViewer.hide();
  selectedItem = null;
});
helpDialog.addEventListener("click", (event) => { if (event.target === helpDialog) helpDialog.close(); });
requireElement<HTMLButtonElement>("#age-accept").addEventListener("click", () => {
  localStorage.setItem(ageStorageKey, "true");
  ageDialog.close();
  store?.confirmAgeAndEnter();
  void loadCatalogAfterAgeConfirmation();
});
requireElement<HTMLButtonElement>("#age-decline").addEventListener("click", () => ageDialog.close());
ageDialog.addEventListener("close", () => {
  store?.setAgeConfirmed(localStorage.getItem(ageStorageKey) === "true");
});

setupCustomCursor();
setupJoystick();

applyLanguage();
store = new StoreScene({
  canvas,
  onProductSelect: openProduct,
  onAgeConfirmed: requestAgeConfirmation,
  onProductUnavailable: () => {},
  onNavigationHover: () => {},
  onReady: () => window.setTimeout(() => sceneLoader.classList.add("is-hidden"), 180),
  onError: () => {
    sceneLoader.classList.add("is-hidden");
    requireElement<HTMLElement>("#fatal-message").hidden = false;
  },
});
store.setQuality(quality);
store.setAgeConfirmed(ageWasConfirmed);
store.setFloorGuidesHidden(floorGuidesHidden);
store.setLanguage(language);
void store.start();
// Preload the catalog and jacket textures while the visitor approaches the
// curtain so the shelves are already stocked after confirmation.
void beginCatalogWarmup();
if (ageWasConfirmed) void loadCatalogAfterAgeConfirmation();

window.addEventListener("beforeunload", () => {
  if (dailyRefreshTimer !== null) window.clearTimeout(dailyRefreshTimer);
  caseViewer.destroy();
  store?.destroy();
});
